// A dashboard's widget renders, kept (.claude/features/12900-rms-dashboards.md, "Rendering and speed").
//
// Each widget's render is a react-query entry keyed by organization, the dashboard, what its
// saved row says the widgets read (everything but the layout), the page context the widget rendered
// under (the dashboard as its view with the viewer's zone and any live clauses by kind), the
// component, its pinned revision and its table requests. An entry holds the widget's
// document and when it was rendered (the server's `cached_at` for an answer from its render
// cache), which the footer reads; its own update time is when this page received it, which the
// fresh window reads. The dashboard page, Home, Slides, Present and the Update widget
// dialog read the same key, so a reader moving between them, between tabs, or back to a
// dashboard they just left sees the widgets at once.
//
// An answer under `DASHBOARD_WIDGET_FRESH_MS` old is the widget's answer: it paints and nothing is
// sent. An older one, kept for `DASHBOARD_WIDGET_KEPT_MS`, paints at once, marked busy, while the
// widget renders again behind it (`ComponentFrame`'s `prefetched`). This replaces, for dashboards
// only, the ten-second window `cardPrefetch.ts` keeps for record pages (#11849): a dashboard is
// a report a reader moves around in and comes back to, its footer says how old its numbers are,
// and Refresh renders every widget again in one click.
//
// The store itself (the windows, reading and keeping an entry) is `Components/keptRenders.ts`,
// which the widget management system's previews use too; this module is the dashboards' keys.

import {
  hashKey,
  skipToken,
  useQueries,
  type QueryClient,
} from "@tanstack/react-query";
import type { PageSelector } from "../Components/builderTypes";
import type { PrefetchedCard } from "../Components/cardPrefetch";
import {
  KEPT_RENDER_FRESH_MS,
  KEPT_RENDER_KEPT_MS,
  cachedRender,
  canonical,
  keptRenderOf,
  rememberRender,
  rememberRenderFailure,
  shownRender,
  type KeptRender,
} from "../Components/keptRenders";
import {
  isRefusal,
  type KeptAnswer,
  type PlacedComponent,
} from "../Components/pageRenderBatch";
import type { ComponentDocument, TableRequest } from "../Components/types";
import type {
  DashboardWidget,
  DashboardView,
  DashboardViewConfig,
} from "./data";

/** How long a widget's answer stands as its own, with no render. */
export const DASHBOARD_WIDGET_FRESH_MS = KEPT_RENDER_FRESH_MS;
/** How long an older answer is kept to paint while the widget renders again. */
export const DASHBOARD_WIDGET_KEPT_MS = KEPT_RENDER_KEPT_MS;
/**
 * How old a widget's numbers may be and still read as up to date: the server render cache's fresh
 * window (`RMS_RENDER_CACHE_TTL_S`, 300 s), past which it renders the widget again. One threshold
 * for the footer's "up to date" and for what a page back in view asks for again (round 10 U5).
 */
export const DASHBOARD_WIDGET_UP_TO_DATE_MS = 5 * 60_000;

/** Every dashboard widget render. */
export const DASHBOARD_WIDGETS_KEY = ["rms-dashboard-card"] as const;

/** A kept widget render: its document, and when it was rendered (epoch ms). */
export type KeptWidget = KeptRender;

/**
 * An answer as kept: a cached one dated by the server's render, a live one by now. A server
 * clock ahead of this one never dates a widget in the future.
 */
export const keptWidgetOf = (answer: KeptAnswer): KeptWidget =>
  keptRenderOf(answer);

/**
 * What a dashboard's saved row says one tab's widgets read, as one key: its config but the layout
 * and what only its tile shows (the kind, and the sorts, flat and as-of settings the view read
 * follows), with that tab's own filters (dashboard-assistant-polish-4 requirement 5). A save that
 * changes any of it is a new page for that tab's widgets; one that leaves it alone (a layout
 * change, a widget's version or look, another tab's filters) keeps every kept render of the tab
 * (round 10 U5, as the server's `saved_page_version`). A tab the row does not hold (a draft's new
 * tab) reads no filters of its own. Arrays keep their order, since a clause's join reads the one
 * before it; object keys are put in one order.
 */
export const savedReadKey = (
  view: Pick<DashboardView, "config">,
  tabId: string
): string => {
  const read: Partial<DashboardViewConfig> = { ...view.config };
  delete read.dashboard;
  delete read.description;
  delete read.icon;
  delete read.color;
  const tab = view.config.dashboard.tabs.find((t) => t.id === tabId);
  return JSON.stringify(
    canonical({ ...read, tab_filters: tab?.filters ?? [] })
  );
};

/** A first load asks for no table pages. */
export const FIRST_LOAD_TABLES: Readonly<Record<string, TableRequest>> = {};

/** A dashboard widget as a render places it: its component at the revision it is pinned to. */
export const placedOf = (
  widget: Pick<DashboardWidget, "component_id" | "revision_id">
): PlacedComponent => ({
  id: widget.component_id,
  current_revision_id: widget.revision_id,
});

/** Every widget render of one dashboard, for Refresh to mark stale (`markStale`). */
export const dashboardWidgetsKey = (
  orgId: string | null | undefined,
  viewId: string
) => [...DASHBOARD_WIDGETS_KEY, orgId ?? null, viewId] as const;

/**
 * One widget's render on one dashboard under one page context, for one saved read of its tab
 * (`savedRead`, `savedReadKey(view, tabId)`): what the saved row says is part of the page the
 * tab's widgets read but not of the context sent, so a save that changes it, or another person's
 * newer row that did, starts that tab's widgets over rather than painting what rendered before,
 * and leaves every other tab's alone (round 10 U5).
 */
export const dashboardWidgetKey = (
  orgId: string | null | undefined,
  viewId: string,
  savedRead: string,
  context: PageSelector,
  component: PlacedComponent,
  tables: Readonly<Record<string, TableRequest>> = FIRST_LOAD_TABLES
) =>
  [
    ...dashboardWidgetsKey(orgId, viewId),
    savedRead,
    context,
    component.id,
    component.current_revision_id,
    tables,
  ] as const;

/** Where a dashboard's widget renders are kept: the organization, the dashboard, its saved read. */
export interface WidgetKeys {
  orgId: string | null | undefined;
  viewId: string;
  savedRead: string;
}

/** Where `dashboardWidgetKey` puts the saved read, for dropping the entries of another. */
export const SAVED_READ_KEY_INDEX = DASHBOARD_WIDGETS_KEY.length + 2;

/**
 * The tab a widget key rendered under (its page context's `tab_id`): which tab's saved read
 * (`savedReadKey`) the key's entry belongs to.
 */
export const tabOfWidgetKey = (key: readonly unknown[]): string =>
  // Every dashboard widget renders under its tab, so every key's context names one.
  (key[SAVED_READ_KEY_INDEX + 1] as { tab_id: string }).tab_id;

/** One widget's key under `keys`, rendered under `context`. */
export const widgetKeyOf = (
  keys: WidgetKeys,
  context: PageSelector,
  component: PlacedComponent,
  tables: Readonly<Record<string, TableRequest>> = FIRST_LOAD_TABLES
) =>
  dashboardWidgetKey(
    keys.orgId,
    keys.viewId,
    keys.savedRead,
    context,
    component,
    tables
  );

/**
 * What a mounting widget can paint: its fresh answer, an older one to show while it renders
 * again, or one still on its way (a tab prefetch) to join.
 */
export const cachedWidget = (
  queryClient: QueryClient,
  key: readonly unknown[]
): PrefetchedCard | null => cachedRender(queryClient, key);

/**
 * The document a widget shows now, read once its render has answered: its kept answer, or null
 * when its latest render failed (the widget shows its failure, not an older answer).
 */
export const shownDocument = (
  queryClient: QueryClient,
  key: readonly unknown[]
): ComponentDocument | null => shownRender(queryClient, key);

const uniqueBy = <T>(items: readonly T[], keyOf: (item: T) => string): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = keyOf(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** What a tab's kept widget renders say about it. */
export interface TabRenders {
  /**
   * When the oldest widget that has answered was rendered (epoch ms; the server's `cached_at` for
   * a cached answer); null while none has.
   */
  oldest: number | null;
  /**
   * Every widget has its answer for now: a fresh one (under the fresh window), or a failure. A
   * widget painting an older answer while it renders again behind it has not settled.
   */
  settled: boolean;
  /**
   * A widget is painting a kept answer marked stale (a Refresh asked for it again, or the server
   * answered from an old cache entry) while its fresh render is on its way: the toolbar's one
   * "Refreshing" state (dashboard-assistant-polish-3 requirement 11).
   */
  refreshing: boolean;
}

/**
 * The page footer's "rendered <time>" and whether the tab's renders have settled, read from the
 * kept entries without ever fetching them. A widget whose summary was refused or failed never
 * renders: its frame keeps that as the widget's failure (`rememberFailure`), so it settles too.
 */
export const useTabRenders = (
  widgetKeys: readonly (readonly unknown[])[]
): TabRenders =>
  useQueries({
    // One observer per widget render: a tab may place the same component twice.
    queries: uniqueBy(widgetKeys, (key) => hashKey(key)).map((key) => ({
      queryKey: key,
      queryFn: skipToken,
      gcTime: DASHBOARD_WIDGET_KEPT_MS,
    })),
    combine: (results): TabRenders => {
      let oldest = Infinity;
      let settled = results.length > 0;
      let refreshing = false;
      for (const result of results) {
        // A widget render key only ever holds a kept widget (`rememberWidget`, the tab prefetch).
        const kept = result.data as KeptWidget | undefined;
        if (kept) oldest = Math.min(oldest, kept.renderedAt);
        const fresh =
          result.dataUpdatedAt > 0 &&
          Date.now() - result.dataUpdatedAt < DASHBOARD_WIDGET_FRESH_MS;
        // A widget that stays on its kept answer after its render failed (#15213) has answered.
        const failed =
          (result.status === "error" &&
            result.errorUpdatedAt >= result.dataUpdatedAt) ||
          kept?.renderFailed === true;
        // Stale (a Refresh, an old cache entry) or asked for again behind its value (a page
        // back in view, round 10 U5): either way its new answer is still on its way.
        const marked =
          (kept?.stale === true || kept?.revalidating === true) && !failed;
        if (marked) refreshing = true;
        if (result.isFetching || marked || !(fresh || failed)) settled = false;
      }
      return {
        oldest: Number.isFinite(oldest) ? oldest : null,
        settled,
        refreshing,
      };
    },
  });

/**
 * The kinds a dashboard's widgets read (#13380, requirement 20): the union of what their kept
 * renders say each read, or null while no kept render says (none has answered yet, or the server
 * names none), when nothing can be said about relevance.
 */
export const useKindsRead = (
  widgetKeys: readonly (readonly unknown[])[]
): ReadonlySet<string> | null =>
  useQueries({
    queries: uniqueBy(widgetKeys, (key) => hashKey(key)).map((key) => ({
      queryKey: key,
      queryFn: skipToken,
      gcTime: DASHBOARD_WIDGET_KEPT_MS,
    })),
    combine: (results): ReadonlySet<string> | null => {
      let known = false;
      const kinds = new Set<string>();
      for (const result of results) {
        const kept = result.data as KeptWidget | undefined;
        if (!kept || !Array.isArray(kept.kindsRead)) continue;
        known = true;
        for (const kind of kept.kindsRead) kinds.add(kind);
      }
      return known ? kinds : null;
    },
  });

/**
 * The kept render of each of `widgetKeys`, in their order (undefined where none is kept yet): what
 * a widget's first render showed, to fit its size to, and whether it is the first to have recorded
 * its revision's kind with no one having acted on it yet (#13380).
 */
export const useKeptWidgets = (
  widgetKeys: readonly (readonly unknown[])[]
): ReadonlyArray<KeptWidget | undefined> => {
  const kept = useQueries({
    queries: uniqueBy(widgetKeys, (key) => hashKey(key)).map((key) => ({
      queryKey: key,
      queryFn: skipToken,
      gcTime: DASHBOARD_WIDGET_KEPT_MS,
    })),
    combine: (results) =>
      results.map((result) => result.data as KeptWidget | undefined),
  });
  // Two widgets pinning the same revision share one key, so one kept render.
  const byKey = new Map(
    uniqueBy(widgetKeys, (key) => hashKey(key)).map(
      (key, index) => [hashKey(key), kept[index]] as const
    )
  );
  return widgetKeys.map((key) => byKey.get(hashKey(key)));
};

/**
 * Marks the kept renders of `widgetKeys` as acted on: their recorded kind no longer asks for
 * anything, here or on a later visit. Their age stays as it was.
 */
export const consumeKindRecorded = (
  queryClient: QueryClient,
  widgetKeys: readonly (readonly unknown[])[]
): void => {
  for (const key of widgetKeys) {
    const state = queryClient.getQueryState<KeptWidget>(key);
    if (!state?.data?.kindRecorded) continue;
    queryClient.setQueryData<KeptWidget>(
      key,
      { ...state.data, kindRecorded: false },
      {
        updatedAt: state.dataUpdatedAt,
      }
    );
  }
};

/**
 * Keeps a widget's failed render as its entry's error, so a background prefetch does not ask
 * for it again until a Refresh (`markStale`) or a new page drops it. An entry with a fetch of its own in
 * flight (a prefetch of the same widget, from another tab that places it too) is left to that
 * fetch, which records its own outcome: the two share one placement in the page's batcher, so
 * that outcome is this one, and writing an idle state under the fetch would end it early.
 *
 * An entry that holds a kept answer keeps it instead (#15213): the widget stays on that answer
 * when the render behind it fails, so a remount, another tab, Deep Dive and export use it, and
 * the widget renders again behind it next time. The answer is marked `renderFailed`, so the page
 * counts the widget as answered rather than still refreshing, and no longer asked for again
 * (a Refresh's dimming, a page back in view), its age and stale mark left as they were. A
 * refusal (`isRefusal`: the render line, the batch or the widget's summary refused) is a
 * denial, not a failed render: it is recorded as the error whatever the entry held.
 */
export const rememberFailure = (
  queryClient: QueryClient,
  key: readonly unknown[],
  error: Error
): void => {
  const state = queryClient.getQueryState<KeptWidget>(key);
  const kept = state?.data;
  if (!state || !kept || isRefusal(error)) {
    rememberRenderFailure(queryClient, key, error);
    return;
  }
  if (state.fetchStatus === "fetching") return;
  queryClient.setQueryData<KeptWidget>(
    key,
    {
      ...kept,
      refreshed: undefined,
      revalidating: undefined,
      renderFailed: true,
    },
    { updatedAt: state.dataUpdatedAt }
  );
};

/**
 * Marks kept renders of the dashboard as asked for again, their age and document left as they
 * were.
 *
 * With no `only`, Refresh (dashboard-assistant-polish-3 requirement 11): every kept render is
 * marked stale and refreshed, so each widget keeps its value on screen, dimmed, and renders again
 * past the server's cache until its fresh answer replaces the entry. A render still on its way
 * from before the Refresh (a tab prefetch) is cancelled first, so it cannot land unmarked; a kept
 * failure goes, so it is asked for again.
 *
 * With `only`, a page back in view (round 10 U5): just those renders are marked revalidating, so
 * the page says it is refreshing while each asks the server's cache again behind its value, kept
 * on screen undimmed; every other entry, a render on its way included, is left as it is.
 */
export const markStale = (
  queryClient: QueryClient,
  orgId: string | null | undefined,
  viewId: string,
  only?: readonly (readonly unknown[])[]
): void => {
  const dashboard = dashboardWidgetsKey(orgId, viewId);
  const chosen = only ? new Set(only.map((key) => hashKey(key))) : null;
  if (!chosen) void queryClient.cancelQueries({ queryKey: dashboard });
  for (const query of queryClient
    .getQueryCache()
    .findAll({ queryKey: dashboard })) {
    const { state } = query;
    if (chosen && !chosen.has(query.queryHash)) continue;
    if (
      !chosen &&
      state.status === "error" &&
      state.errorUpdatedAt >= state.dataUpdatedAt
    ) {
      queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
      continue;
    }
    const kept = state.data as KeptWidget | undefined;
    if (!kept) continue;
    queryClient.setQueryData<KeptWidget>(
      query.queryKey,
      // Asked for again: a kept answer a failed render left is on its way again.
      chosen
        ? { ...kept, revalidating: true, renderFailed: undefined }
        : { ...kept, stale: true, refreshed: true, renderFailed: undefined },
      { updatedAt: query.state.dataUpdatedAt }
    );
  }
};

/**
 * The kept renders under `keys` whose numbers are older than `DASHBOARD_WIDGET_UP_TO_DATE_MS` (by
 * when they were rendered, the footer's measure), as a page coming back into view finds them
 * (round 10 U5): the ones to ask for again. A render on its way is left to land, and a widget
 * with no kept answer has nothing to refresh.
 */
export const outdatedWidgetKeys = (
  queryClient: QueryClient,
  keys: readonly (readonly unknown[])[],
  now: number
): (readonly unknown[])[] =>
  uniqueBy(keys, (key) => hashKey(key)).filter((key) => {
    const state = queryClient.getQueryState<KeptWidget>(key);
    return (
      state?.data !== undefined &&
      state.fetchStatus !== "fetching" &&
      now - state.data.renderedAt >= DASHBOARD_WIDGET_UP_TO_DATE_MS
    );
  });

/**
 * Keeps a widget's answer as its entry. `receivedAt` (now, unless a story ages it) dates the
 * entry for the fresh window; the answer's own render time goes with it for the footer.
 */
export const rememberWidget = (
  queryClient: QueryClient,
  key: readonly unknown[],
  widget: KeptWidget,
  receivedAt: number = Date.now()
): void => rememberRender(queryClient, key, widget, receivedAt);
